import { useRef, useCallback, useEffect, useId } from "react";
import { motion } from "framer-motion";

/**
 * TiltCard — premium micro-interaction wrapper.
 *
 * The outer element is the fixed pointer hitbox. Only the inner card moves.
 * Keeping pointer tracking on the stationary wrapper prevents the transformed
 * card from repeatedly triggering pointer leave/enter events near its edges.
 */
const TiltCard = ({
  max = 10,
  lift = 8,
  scale = 1.02,
  parallaxChildren = false,
  spotlight = false,
  spotlightColor = "rgba(118,185,0,0.28)",
  className = "",
  outerClassName = "",
  testid,
  children,
  variants,
  ...rest
}) => {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const rafRef = useRef(0);
  const hoverActiveRef = useRef(false);
  const tiltId = useId();

  const syncGlobalHover = useCallback(
    (active) => {
      if (hoverActiveRef.current === active) return;
      hoverActiveRef.current = active;

      if (typeof window === "undefined") return;

      // Keep a shared count so moving directly between two cards does not make
      // the background flash back on between their leave/enter events.
      const body = document.body;
      const currentCount = Number.parseInt(body.dataset.tiltHoverCount || "0", 10) || 0;
      const nextCount = active ? currentCount + 1 : Math.max(0, currentCount - 1);

      body.dataset.tiltHoverCount = String(nextCount);
      body.classList.toggle("tilt-hovering", nextCount > 0);

      window.dispatchEvent(
        new CustomEvent("tilt:hover", {
          detail: { active, id: tiltId },
        })
      );
    },
    [tiltId]
  );

  const onPointerMove = useCallback(
    (event) => {
      if (event.pointerType !== "mouse") return;

      const outer = outerRef.current;
      const inner = innerRef.current;
      if (!outer || !inner) return;

      // Always measure the stationary wrapper, never the transformed card.
      const rect = outer.getBoundingClientRect();
      if (!rect.width || !rect.height) return;

      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      const px = Math.min(1, Math.max(0, x / rect.width));
      const py = Math.min(1, Math.max(0, y / rect.height));
      const rx = (0.5 - py) * max * 2;
      const ry = (px - 0.5) * max * 2;

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = 0;
        if (innerRef.current !== inner) return;

        inner.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
        inner.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
        inner.style.setProperty("--mx", `${x.toFixed(0)}px`);
        inner.style.setProperty("--my", `${y.toFixed(0)}px`);
        inner.style.setProperty("--mx-pct", `${(px * 100).toFixed(1)}%`);
        inner.style.setProperty("--my-pct", `${(py * 100).toFixed(1)}%`);
        inner.style.setProperty("--sheen-x", `${(px * 100).toFixed(1)}%`);
        inner.style.setProperty("--sheen-y", `${(py * 100).toFixed(1)}%`);
        inner.style.setProperty("--tx", `${(ry * 0.35).toFixed(2)}px`);
        inner.style.setProperty("--ty", `${(-rx * 0.35).toFixed(2)}px`);
        inner.style.setProperty("--hovered", "1");
      });
    },
    [max]
  );

  const onPointerEnter = useCallback(
    (event) => {
      if (event.pointerType !== "mouse") return;

      const inner = innerRef.current;
      if (!inner) return;

      // Fast response while tracking the cursor; the slow easing is reserved
      // for the return-to-rest animation when the pointer leaves.
      inner.style.transition =
        "transform 80ms linear, box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), border-color 220ms ease";
      inner.style.setProperty("--content-tilt-duration", "90ms");
      inner.style.setProperty("--content-tilt-ease", "linear");
      inner.style.setProperty("--hovered", "1");

      // The moving inner card can stop matching the CSS :hover pseudo-class.
      // Pin the green border to the stable outer hitbox hover lifecycle instead.
      inner.style.borderColor = "rgba(118, 185, 0, 0.55)";

      syncGlobalHover(true);
    },
    [syncGlobalHover]
  );

  const resetTilt = useCallback(() => {
    const inner = innerRef.current;
    if (!inner) return;

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }

    inner.style.transition =
      "transform 460ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 460ms cubic-bezier(0.22, 1, 0.36, 1), border-color 220ms ease";
    inner.style.setProperty("--content-tilt-duration", "420ms");
    inner.style.setProperty("--content-tilt-ease", "cubic-bezier(0.22, 1, 0.36, 1)");
    inner.style.setProperty("--rx", "0deg");
    inner.style.setProperty("--ry", "0deg");
    inner.style.setProperty("--tx", "0px");
    inner.style.setProperty("--ty", "0px");
    inner.style.setProperty("--hovered", "0");
    inner.style.removeProperty("border-color");
    syncGlobalHover(false);
  }, [syncGlobalHover]);

  const onPointerLeave = useCallback(
    (event) => {
      if (event.pointerType !== "mouse") return;
      resetTilt();
    },
    [resetTilt]
  );

  useEffect(
    () => () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (hoverActiveRef.current) syncGlobalHover(false);
    },
    [syncGlobalHover]
  );

  return (
    <motion.div
      {...rest}
      ref={outerRef}
      variants={variants}
      data-testid={testid}
      onPointerMove={onPointerMove}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerCancel={resetTilt}
      style={{
        perspective: "1400px",
        transformStyle: "preserve-3d",
        "--lift": `${lift}px`,
        "--scale": scale,
      }}
      className={`tilt-outer ${outerClassName}`}
    >
      <div
        ref={innerRef}
        className={`tilt-card ${spotlight ? "with-spotlight" : ""} ${className}`}
      >
        <div
          className="tilt-content relative z-[2] h-full w-full flex flex-col"
          style={
            parallaxChildren
              ? {
                  transform: "translate3d(var(--tx,0px), var(--ty,0px), 30px)",
                  transition:
                    "transform var(--content-tilt-duration, 350ms) var(--content-tilt-ease, cubic-bezier(0.22, 1, 0.36, 1))",
                  transformStyle: "preserve-3d",
                }
              : undefined
          }
        >
          {children}
        </div>

        <span
          aria-hidden
          className="tilt-sheen pointer-events-none absolute inset-0 rounded-[inherit]"
        />

        {spotlight && (
          <>
            <span
              aria-hidden
              className="tilt-halo pointer-events-none absolute inset-0 rounded-[inherit]"
              style={{
                background: `radial-gradient(520px circle at var(--mx,50%) var(--my,50%), ${spotlightColor}, transparent 55%)`,
              }}
            />
            <span
              aria-hidden
              className="tilt-core pointer-events-none absolute inset-0 rounded-[inherit]"
              style={{
                background:
                  "radial-gradient(180px circle at var(--mx,50%) var(--my,50%), rgba(140,229,0,0.45), transparent 65%)",
              }}
            />
            <span
              aria-hidden
              className="tilt-border pointer-events-none absolute inset-0 rounded-[inherit]"
            />
          </>
        )}
      </div>
    </motion.div>
  );
};

export default TiltCard;
